import { type AssetType, getAssetChannelLabel, getAssetLabel, isAssetType } from 'src/data/asset';
// The source-type constant comes from its own module rather than the `src/data/generation` barrel,
// which reaches back into this folder. For the same reason this module is not re-exported from
// `src/data/project/index.ts`: doing so pulls the `src/data/generationTool` barrel below in ahead of
// the rest of the folder, and the cycle surfaces at runtime as an undefined import.
import { GENERATION_SOURCE_TYPE } from 'src/data/generation/const';
import {
  convertRewriteToolToSuggestTool,
  doesToolSupportsSubTemplates,
  type GenerationTool,
  getToolContentType,
  getToolDescription,
  getToolTitle,
  isBasicContentImproverTool,
  isFreestyleGenerationTool,
  isGenerationTool,
  isImageGenerationTool,
  isLongFormLocalizationTool,
  isMixtureTool,
  isMultiPromptTool,
  isRewriteGenerationTool,
} from 'src/data/generationTool';
import { AI_CHAT_LABEL, CONTENT_IMPROVER_LABEL } from 'src/data/ui';

import type { FeedAssetGroup } from './types';

export interface AssetGroupDescription {
  title: string;
  description: string;
  source: string;
  /**
   * Whether `title` names what the group produced rather than how it was generated. A hint that
   * reads "<this> was saved" can only use the former, and the title alone no longer says which it
   * is — it used to, by carrying a "Template – " prefix wherever it named the output.
   */
  namesOutput: boolean;
}

/**
 * How a feed group describes its own generation — the group counterpart to
 * `getGenerationResultDescription`, which answers the same question for a generation *request*.
 *
 * A group carries the whole answer with it, so nothing else is passed in. The request version
 * cannot: a request holds only the template's id, which is why it takes the template separately.
 */
export const getAssetGroupDescription = (group: FeedAssetGroup): AssetGroupDescription => {
  // A group's tool and asset type arrive as backend enums, wider than the ones the tool helpers
  // accept. Narrowing here is what the request-shaped path papered over with a cast.
  const generationTool = asTool(group.generationTool);
  const chainGenerationTool = asTool(group.chainGenerationTool);
  const assetType = getGroupAssetType(group);
  const alignedTool = generationTool ? convertRewriteToolToSuggestTool(generationTool) : undefined;

  // A saved template names the group it produced. That tool declares no sub-templates, so the name
  // is preferred here rather than left to the tool-title fallback.
  const templateName = group.customTemplate?.name;
  const named = (tool?: GenerationTool) => templateName || toolTitle(tool);

  let title = '';
  let description = '';
  let namesOutput = true;

  if (group.sourceType === GENERATION_SOURCE_TYPE.aiChat) {
    title = AI_CHAT_LABEL;
    namesOutput = false;
  } else if (isContentImprover(alignedTool, generationTool)) {
    title = CONTENT_IMPROVER_LABEL;
    namesOutput = false;
  } else if (chainGenerationTool) {
    // Reached through the chain tool, but named after the group's own tool — the chain says how the
    // generation ran, not what it produced.
    if (templateName || supportsSubTemplates(chainGenerationTool)) {
      title = named(generationTool);
    } else if (isFreestyleGenerationTool(chainGenerationTool)) {
      title = getToolContentType(chainGenerationTool) || '';
      namesOutput = false;
    } else {
      title = toolTitle(chainGenerationTool);
    }
  } else if (alignedTool && isLongFormLocalizationTool(alignedTool)) {
    title = 'General prompt';
    namesOutput = false;
  } else if (alignedTool) {
    const templated = Boolean(templateName) || supportsSubTemplates(generationTool);

    if (templated) {
      title = named(generationTool);
    } else {
      title = getToolContentType(alignedTool) || '';
    }

    description = getToolDescription(alignedTool) || '';

    // A tool that generates a set rather than a single asset has no one asset to name.
    if (!templated && !generatesASet(alignedTool, generationTool)) {
      title += ` - ${assetLabel(assetType)}`;
    }
  } else if (templateName || supportsSubTemplates(generationTool)) {
    title = named(generationTool);
    description = assetLabel(assetType);
  } else {
    title = assetChannelLabel(assetType);
    description = assetLabel(assetType);
  }

  return { title, description, source: describeSource(group.sourceType), namesOutput };
};

const isContentImprover = (
  alignedTool?: GenerationTool,
  generationTool?: GenerationTool
): boolean =>
  Boolean(
    (alignedTool && isBasicContentImproverTool(alignedTool)) ||
    (generationTool && isRewriteGenerationTool(generationTool))
  );

const generatesASet = (alignedTool: GenerationTool, generationTool?: GenerationTool): boolean =>
  isMixtureTool(alignedTool) ||
  isMultiPromptTool(alignedTool) ||
  isImageGenerationTool(alignedTool) ||
  isFreestyleGenerationTool(alignedTool) ||
  Boolean(generationTool && isRewriteGenerationTool(generationTool));

const asTool = (tool?: string | null): GenerationTool | undefined =>
  isGenerationTool(tool) ? tool : undefined;

// Every asset in a group shares one type, so the first one answers for all of them.
const getGroupAssetType = (group: FeedAssetGroup): AssetType | undefined => {
  const firstAsset = group.assets[0];
  const assetType = firstAsset && 'assetType' in firstAsset ? firstAsset.assetType : undefined;

  return isAssetType(assetType) ? assetType : undefined;
};

const supportsSubTemplates = (tool?: GenerationTool): boolean =>
  Boolean(tool && doesToolSupportsSubTemplates(tool));

const toolTitle = (tool?: GenerationTool): string => (tool ? getToolTitle(tool) || '' : '');

const assetLabel = (type?: AssetType): string => (type ? getAssetLabel(type) : '');

const assetChannelLabel = (type?: AssetType): string =>
  (type ? getAssetChannelLabel(type) : '') || '';

const describeSource = (sourceType?: string | null): string => {
  switch (sourceType) {
    case GENERATION_SOURCE_TYPE.improveListScore:
      return CONTENT_IMPROVER_LABEL;
    case GENERATION_SOURCE_TYPE.changeTov:
      return 'More like this - change tone of voice';
    default:
      return '';
  }
};
